import React, { useEffect, useState } from "react";
import { Button, Modal } from "./ui";
import client, { API, apiErr } from "../lib/api";
import { Paperclip, Upload, Trash2, FileText, Loader2, ExternalLink } from "lucide-react";

const token = () => localStorage.getItem("suite_token") || "";
const fileUrl = (id) => `${API}/attachments/file/${id}?auth=${encodeURIComponent(token())}`;

// Reusable per-record photo & document attachments. `coll` is the backend collection
// (holdings/persons/currencies/benefits/liabilities); `recordId` is that record's id.
export function AttachmentsButton({ coll, recordId, label = "Photos & docs", accent = null, compact = false }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await client.get(`/attachments/${coll}/${recordId}`); setRows(data || []); }
    catch (e) { setErr(apiErr(e)); }
  };
  useEffect(() => { if (open) { setErr(""); load(); } }, [open]); // eslint-disable-line

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await client.post(`/attachments/${coll}/${recordId}`, fd);
      await load();
    } catch (e2) { setErr(apiErr(e2)); }
    setBusy(false);
    e.target.value = "";
  };

  const del = async (id) => {
    if (!window.confirm("Remove this attachment?")) return;
    setBusy(true);
    try { await client.delete(`/attachments/${id}`); await load(); }
    catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`attach-open-${recordId}`}
        title="Photos & documents"
        className="inline-flex items-center gap-1 rounded border border-black/15 px-1.5 py-1 text-[11px] hover:bg-black/[0.04] dark:border-white/15 dark:hover:bg-white/[0.06]"
        style={accent ? { color: accent } : undefined}>
        <Paperclip size={13} />{!compact && <span>{label}</span>}{rows.length ? <span className="rounded-full bg-[var(--primary)]/15 px-1 text-[9px] font-semibold text-[var(--primary)]">{rows.length}</span> : null}
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Photos & documents" testid={`attach-modal-${recordId}`}>
        {err && <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300" data-testid="attach-error">{err}</div>}

        <div className="mb-4 flex items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90" data-testid={`attach-upload-${recordId}`}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Add photo / PDF
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onPick} disabled={busy} />
          </label>
          <span className="text-xs opacity-60">Images (JPEG/PNG/WebP) and PDF · up to 15 MB.</span>
        </div>

        {rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-black/15 py-8 text-center text-sm opacity-60 dark:border-white/15" data-testid={`attach-empty-${recordId}`}>
            No attachments yet. Add a photo or an appraisal/receipt PDF.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid={`attach-list-${recordId}`}>
            {rows.map((a) => (
              <div key={a.id} className="group relative overflow-hidden rounded-lg border border-black/10 dark:border-white/10" data-testid={`attach-item-${a.id}`}>
                {a.kind === "image" ? (
                  <a href={fileUrl(a.id)} target="_blank" rel="noreferrer">
                    <img src={fileUrl(a.id)} alt={a.original_filename} className="h-28 w-full object-cover" />
                  </a>
                ) : (
                  <a href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="flex h-28 w-full flex-col items-center justify-center gap-1 bg-black/[0.03] text-[var(--primary)] dark:bg-white/[0.04]">
                    <FileText size={30} /><span className="text-[10px]">PDF <ExternalLink size={9} className="inline" /></span>
                  </a>
                )}
                <div className="flex items-center justify-between gap-1 px-2 py-1">
                  <span className="truncate text-[10px] opacity-70" title={a.original_filename}>{a.original_filename}</span>
                  <button type="button" onClick={() => del(a.id)} disabled={busy} data-testid={`attach-del-${a.id}`} className="text-black/40 hover:text-red-500 dark:text-white/40"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
